import { deepActiveElement } from "#ce/utils/focus.js";

/**
 * Focus handling for sp-dialog. A native modal `<dialog>` traps Tab and inerts
 * the background, but it cannot move focus into content built from custom
 * elements (the inner controls live in nested shadow roots it does not reach)
 * nor restore focus to a non-focusable custom-element host on close. This covers
 * both: remember the opener, move focus inside, and restore it afterwards.
 *
 * This is a pure mechanism with no notion of a dialog session. The dialog
 * lifecycle decides when to capture (once, on open) and restore (on close).
 *
 * @param {import("vue").Ref<HTMLElement | null>} content - The dialog content ref.
 * @returns {{
 *   capture: () => void,
 *   moveIn: () => void,
 *   restore: () => void,
 * }}
 */
export function useDialogFocus(content) {
  let origin = null;

  // Remember the element to restore focus to when the dialog closes.
  function capture() {
    origin = deepActiveElement();
  }

  /**
   * Whether focus already sits inside the dialog content, slotted light-DOM
   * fields included — `contains()` cannot see those, so the walk hops through
   * `assignedSlot` and shadow hosts.
   */
  function focusIsInside() {
    let node = deepActiveElement();
    while (node) {
      if (node === content.value) {
        return true;
      }
      node = node.assignedSlot ?? node.parentNode ?? node.host ?? null;
    }
    return false;
  }

  /**
   * moveIn runs once the enter animation settles, so a field the user focused
   * and started typing into during the animation must keep its focus.
   */
  function moveIn() {
    if (focusIsInside()) {
      return;
    }
    content.value?.focus({ preventScroll: true });
  }

  function restore() {
    origin?.focus?.({ preventScroll: true });
    origin = null;
  }

  return { capture, moveIn, restore };
}
